/**
 * Rabatttyper.
 *
 * Erbjudandena lagras strukturerat i stället för som fri text, av två skäl:
 * dels kan admin då filtrera och få statistik per rabatttyp, dels blir
 * formuleringen i häftet identisk överallt utan att någon behöver skriva den.
 */

export type OfferType = 'percentage' | 'fixedAmount' | 'buyXPayY' | 'other'

export type OfferTerms =
  | { type: 'percentage'; percent: number; minPurchaseSek?: number | null }
  | { type: 'fixedAmount'; amountSek: number; minPurchaseSek?: number | null }
  | { type: 'buyXPayY'; buyQty: number; payQty: number }
  | { type: 'other'; freeText: string }

export const OFFER_TYPE_LABELS: Record<OfferType, string> = {
  percentage: 'Procentrabatt',
  fixedAmount: 'Fast belopp',
  buyXPayY: 'Köp X betala Y',
  other: 'Annat',
}

const sek = (n: number) => new Intl.NumberFormat('sv-SE').format(n)

/**
 * Genererar den mening som visas på erbjudandets sida i häftet.
 * Samma funktion används i Excel-exporten så att tryck och app säger samma sak.
 */
export function describeOffer(terms: OfferTerms): string {
  switch (terms.type) {
    case 'percentage':
      return terms.minPurchaseSek
        ? `${terms.percent} % rabatt vid köp över ${sek(terms.minPurchaseSek)} kr`
        : `${terms.percent} % rabatt`
    case 'fixedAmount':
      return terms.minPurchaseSek
        ? `${sek(terms.amountSek)} kr rabatt vid köp över ${sek(terms.minPurchaseSek)} kr`
        : `${sek(terms.amountSek)} kr rabatt`
    case 'buyXPayY':
      return `Köp ${terms.buyQty} betala för ${terms.payQty}`
    case 'other':
      return terms.freeText
  }
}

/** Kort variant för listvyn och sökträffar, t.ex. "20 %" eller "100 kr". */
export function shortOfferLabel(terms: OfferTerms): string {
  switch (terms.type) {
    case 'percentage':
      return `${terms.percent} %`
    case 'fixedAmount':
      return `${sek(terms.amountSek)} kr`
    case 'buyXPayY':
      return `${terms.buyQty} för ${terms.payQty}`
    case 'other':
      return 'Erbjudande'
  }
}

/** Validering som körs både i admin-formuläret och i Cloud Functions. */
export function validateOfferTerms(terms: OfferTerms): string | null {
  switch (terms.type) {
    case 'percentage':
      if (!Number.isFinite(terms.percent) || terms.percent <= 0 || terms.percent > 100)
        return 'Procentsatsen måste vara mellan 1 och 100.'
      if (terms.minPurchaseSek != null && terms.minPurchaseSek < 0)
        return 'Köpgränsen kan inte vara negativ.'
      return null
    case 'fixedAmount':
      if (!Number.isFinite(terms.amountSek) || terms.amountSek <= 0)
        return 'Rabattbeloppet måste vara större än noll.'
      if (terms.minPurchaseSek != null && terms.minPurchaseSek < terms.amountSek)
        return 'Köpgränsen bör vara minst lika stor som rabatten.'
      return null
    case 'buyXPayY':
      if (!Number.isInteger(terms.buyQty) || terms.buyQty < 2)
        return 'Antalet man köper måste vara minst 2.'
      if (!Number.isInteger(terms.payQty) || terms.payQty < 1)
        return 'Antalet man betalar för måste vara minst 1.'
      if (terms.payQty >= terms.buyQty)
        return 'Man måste betala för färre än man köper, annars är det ingen rabatt.'
      return null
    case 'other':
      if (!terms.freeText?.trim()) return 'Skriv vad erbjudandet innebär.'
      if (terms.freeText.length > 120) return 'Håll texten under 120 tecken.'
      return null
  }
}

/**
 * Samlar ett företags erbjudanden på ett ställe.
 *
 * Ordningsnumret sätts när erbjudandet skapas, så ett nytt erbjudande hamnade
 * sist i häftet – långt från företagets andra. I pappershäftet står ett
 * företags kuponger bredvid varandra, och den som bläddrar letar efter dem
 * där.
 *
 * Grupperingen görs när erbjudandena visas, inte genom att skriva om numren i
 * databasen. Då rättas även de som redan ligger fel, utan att varje flytt
 * blir en post i granskningsloggen. Ett företag hamnar där dess första
 * erbjudande står, och inom företaget gäller ordningsnumret som förut.
 *
 * Listan måste redan vara sorterad efter ordningsnummer – det är den ordning
 * databasen levererar – så att "första" betyder det som skapades först.
 */
export function groupOffersByCompany<T extends { companyId: string }>(offers: T[]): T[] {
  const groups = new Map<string, T[]>()
  for (const offer of offers) {
    const group = groups.get(offer.companyId)
    if (group) group.push(offer)
    else groups.set(offer.companyId, [offer])
  }
  return [...groups.values()].flat()
}
